import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import GroupIcon from '@mui/icons-material/ViewModuleOutlined';
import CopyIcon from '@mui/icons-material/ContentCopyOutlined';
import OpenIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import { PageHeader, Section } from '../components/Section';
import { format, useTranslation } from '../i18n';
import {
  DEFAULT_TTL_SECONDS,
  TTL_CHOICES,
  createSharedGroup,
  deleteSharedGroup,
  deleteSharedVideo,
  describeExpiry,
  fetchShares,
  formatBytes,
  uploadSharedVideo,
  type SharedGroup,
  type SharedVideo,
  type SharesIndex,
  type UploadProgress,
} from '../lib/shares';

const COLUMN_CHOICES: (number | null)[] = [null, 1, 2, 3, 4, 5, 6];

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function ttlValue(seconds: number | null): string {
  return seconds == null ? 'never' : String(seconds);
}
function ttlFromValue(v: string): number | null {
  return v === 'never' ? null : Number(v);
}

function TtlSelect({ value, onChange, disabled }: { value: number | null; onChange: (s: number | null) => void; disabled?: boolean }) {
  const t = useTranslation();
  return (
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel id="ttl-label">{t.videoShares.linkExpires}</InputLabel>
      <Select labelId="ttl-label" label={t.videoShares.linkExpires} value={ttlValue(value)} onChange={(e) => onChange(ttlFromValue(String(e.target.value)))}>
        {TTL_CHOICES.map((c) => (
          <MenuItem key={c.key} value={ttlValue(c.seconds)}>
            {t.videoShares.ttl[c.key]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function ExpiryChip({ expiresAt, expired }: { expiresAt: string | null; expired: boolean }) {
  const t = useTranslation();
  if (expired) return <Chip size="small" color="error" variant="outlined" label={t.videoShares.expired} sx={{ height: 20, fontSize: 11 }} />;
  const label = describeExpiry(expiresAt, t.videoShares.expiry);
  return (
    <Tooltip title={expiresAt ? fmtDate(expiresAt) : t.videoShares.noExpiry} arrow>
      <Chip size="small" variant="outlined" label={label} sx={{ height: 20, fontSize: 11, color: expiresAt ? 'text.secondary' : 'success.main' }} />
    </Tooltip>
  );
}

function LinkActions({ url, onCopied }: { url: string; onCopied: (ok: boolean) => void }) {
  const t = useTranslation();
  return (
    <Stack direction="row" spacing={0} justifyContent="flex-end">
      <Tooltip title={t.videoShares.copyLink} arrow>
        <IconButton size="small" onClick={() => void copyText(url).then(onCopied)} aria-label="copy link">
          <CopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t.videoShares.open} arrow>
        <IconButton size="small" component="a" href={url} target="_blank" rel="noopener" aria-label="open link">
          <OpenIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

// ── Upload dialog ───────────────────────────────────────────────────────────
type Pending = {
  file: File;
  title: string;
  state: 'queued' | 'uploading' | 'done' | 'error';
  progress: UploadProgress;
  error?: string;
  result?: SharedVideo;
};

function UploadDialog({ maxBytes, onClose, onUploaded }: { maxBytes: number; onClose: () => void; onUploaded: () => void }) {
  const t = useTranslation();
  const [items, setItems] = useState<Pending[]>([]);
  const [ttl, setTtl] = useState<number | null>(DEFAULT_TTL_SECONDS);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const anyDone = items.some((i) => i.state === 'done');

  const addFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    setItems((prev) => [
      ...prev,
      ...files.map<Pending>((file) => ({
        file,
        title: file.name.replace(/\.[a-z0-9]+$/i, ''),
        state: 'queued',
        progress: { sent: 0, total: file.size },
        error: file.size > maxBytes ? format(t.videoShares.upload.tooLarge, { max: formatBytes(maxBytes) }) : undefined,
      })),
    ]);
  };

  const patch = (idx: number, p: Partial<Pending>) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...p } : it)));

  const start = async () => {
    setBusy(true);
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.state !== 'queued' || it.error) continue;
      patch(i, { state: 'uploading' });
      try {
        const video = await uploadSharedVideo(it.file, {
          title: it.title,
          ttl_seconds: ttl,
          onProgress: (progress) => patch(i, { progress }),
        });
        patch(i, { state: 'done', result: video, progress: { sent: it.file.size, total: it.file.size } });
        onUploaded();
      } catch (err) {
        patch(i, { state: 'error', error: err instanceof Error ? err.message : String(err) });
      }
    }
    setBusy(false);
  };

  const queued = items.filter((i) => i.state === 'queued' && !i.error).length;

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t.videoShares.upload.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {t.videoShares.upload.intro}
          </Typography>
          <input ref={inputRef} type="file" accept="video/*,.mkv,.mov,.mp4,.webm,.m4v" multiple hidden onChange={addFiles} data-testid="share-file-input" />
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => inputRef.current?.click()} disabled={busy}>
              {t.videoShares.upload.choose}
            </Button>
            <Box sx={{ flex: 1 }}>
              <TtlSelect value={ttl} onChange={setTtl} disabled={busy || anyDone} />
            </Box>
          </Stack>

          {items.length > 0 && (
            <Stack spacing={1.5}>
              {items.map((it, idx) => (
                <Paper key={`${it.file.name}-${idx}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <TextField
                        size="small"
                        label={t.videoShares.upload.titleLabel}
                        value={it.title}
                        onChange={(e) => patch(idx, { title: e.target.value })}
                        disabled={it.state !== 'queued'}
                        fullWidth
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatBytes(it.file.size)}
                      </Typography>
                      {it.state === 'done' && <CheckIcon color="success" fontSize="small" />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {it.file.name}
                    </Typography>
                    {it.state === 'uploading' && (
                      <LinearProgress variant="determinate" value={it.progress.total ? (100 * it.progress.sent) / it.progress.total : 0} />
                    )}
                    {it.error && <Alert severity="error" sx={{ py: 0 }}>{it.error}</Alert>}
                    {it.result && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Link href={it.result.share_url} target="_blank" rel="noopener" variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {it.result.share_url}
                        </Link>
                        <Tooltip title={t.videoShares.copyLink} arrow>
                          <IconButton size="small" onClick={() => void copyText(it.result!.share_url)} aria-label="copy link">
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {anyDone ? t.videoShares.upload.done : t.common.cancel}
        </Button>
        <Button onClick={() => void start()} variant="contained" disabled={busy || queued === 0}>
          {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : queued ? format(t.videoShares.upload.uploadCount, { count: queued }) : t.videoShares.upload.upload}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Group dialog ────────────────────────────────────────────────────────────
function GroupDialog({ videos: initial, onClose, onCreated }: { videos: SharedVideo[]; onClose: () => void; onCreated: () => void }) {
  // Snapshot: the page clears its selection once the group exists, and the
  // dialog must keep showing what went into it.
  const t = useTranslation();
  const [videos] = useState(initial);
  const [title, setTitle] = useState(() => (videos.length === 1 ? videos[0].title : format(t.videoShares.group.defaultTitle, { count: videos.length })));
  const [columns, setColumns] = useState<number | null>(null);
  const [ttl, setTtl] = useState<number | null>(DEFAULT_TTL_SECONDS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<SharedGroup | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const g = await createSharedGroup({ title: title.trim(), video_ids: videos.map((v) => v.id), columns, ttl_seconds: ttl });
      setCreated(g);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t.videoShares.group.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {format(t.videoShares.group.intro, { count: videos.length })}
          </Typography>
          <TextField size="small" label={t.videoShares.group.titleLabel} value={title} onChange={(e) => setTitle(e.target.value)} disabled={busy || !!created} fullWidth />
          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth disabled={busy || !!created}>
              <InputLabel id="cols-label">{t.videoShares.group.layout}</InputLabel>
              <Select
                labelId="cols-label"
                label={t.videoShares.group.layout}
                value={columns == null ? 'auto' : String(columns)}
                onChange={(e) => setColumns(e.target.value === 'auto' ? null : Number(e.target.value))}
              >
                {COLUMN_CHOICES.map((n) => (
                  <MenuItem key={n ?? 'auto'} value={n == null ? 'auto' : String(n)}>
                    {n == null ? t.videoShares.layoutAuto : format(t.videoShares.perRow, { n })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TtlSelect value={ttl} onChange={setTtl} disabled={busy || !!created} />
          </Stack>
          <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto' }}>
            <Table size="small">
              <TableBody>
                {videos.map((v, i) => (
                  <TableRow key={v.id}>
                    <TableCell sx={{ width: 36, color: 'text.secondary' }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{v.title}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {formatBytes(v.size_bytes)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          {error && <Alert severity="error">{error}</Alert>}
          {created && (
            <Alert
              severity="success"
              icon={false}
              action={
                <Button color="inherit" size="small" startIcon={copied ? <CheckIcon /> : <CopyIcon />} onClick={() => void copyText(created.share_url).then(setCopied)}>
                  {copied ? t.videoShares.copied : t.videoShares.copy}
                </Button>
              }
            >
              <Link href={created.share_url} target="_blank" rel="noopener" sx={{ wordBreak: 'break-all' }}>
                {created.share_url}
              </Link>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {created ? t.videoShares.group.done : t.common.cancel}
        </Button>
        {!created && (
          <Button onClick={() => void create()} variant="contained" disabled={busy || videos.length === 0}>
            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t.videoShares.group.create}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function VideoShares() {
  const t = useTranslation();
  const [data, setData] = useState<SharesIndex | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]); // selection order = group order
  const [uploading, setUploading] = useState(false);
  const [grouping, setGrouping] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    try {
      setData(await fetchShares());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const videosById = useMemo(() => new Map((data?.videos ?? []).map((v) => [v.id, v])), [data]);
  const selectedVideos = selected.map((id) => videosById.get(id)).filter((v): v is SharedVideo => !!v);
  const maxGroup = data?.max_group_videos ?? 24;

  const toggle = (id: number) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= maxGroup ? prev : [...prev, id]));
  const toggleAll = () => {
    const live = (data?.videos ?? []).filter((v) => !v.expired).map((v) => v.id);
    setSelected((prev) => (prev.length === live.length ? [] : live.slice(0, maxGroup)));
  };

  const onCopied = (ok: boolean) => setToast(ok ? t.videoShares.linkCopied : t.videoShares.copyFailed);

  const removeVideo = async (v: SharedVideo) => {
    if (!confirm(format(t.videoShares.deleteVideoConfirm, { title: v.title }))) return;
    try {
      await deleteSharedVideo(v.id);
      setSelected((prev) => prev.filter((x) => x !== v.id));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  const removeGroup = async (g: SharedGroup) => {
    if (!confirm(format(t.videoShares.deleteGroupConfirm, { title: g.title }))) return;
    try {
      await deleteSharedGroup(g.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const videos = data?.videos ?? [];
  const groups = data?.groups ?? [];
  const liveCount = videos.filter((v) => !v.expired).length;

  return (
    <Box>
      <PageHeader title={t.videoShares.title} description={t.videoShares.description} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<GroupIcon />}
          disabled={selectedVideos.length === 0}
          onClick={() => setGrouping(true)}
        >
          {selectedVideos.length > 0 ? format(t.videoShares.groupShareCount, { count: selectedVideos.length }) : t.videoShares.groupShare}
        </Button>
        {selectedVideos.length > 0 && (
          <Button size="small" onClick={() => setSelected([])}>
            {t.videoShares.clearSelection}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploading(true)}>
          {t.videoShares.shareMore}
        </Button>
      </Stack>

      {data == null && !error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper variant="outlined" sx={{ mb: 4 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={selected.length > 0 && selected.length < liveCount}
                        checked={liveCount > 0 && selected.length === liveCount}
                        onChange={toggleAll}
                        inputProps={{ 'aria-label': t.videoShares.selectAll }}
                      />
                    </TableCell>
                    <TableCell>{t.videoShares.table.video}</TableCell>
                    <TableCell align="right">{t.videoShares.table.size}</TableCell>
                    <TableCell>{t.videoShares.table.shared}</TableCell>
                    <TableCell>{t.videoShares.table.expires}</TableCell>
                    <TableCell align="right">{t.videoShares.table.link}</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {videos.map((v) => {
                    const idx = selected.indexOf(v.id);
                    return (
                      <TableRow key={v.id} hover selected={idx >= 0} sx={{ opacity: v.expired ? 0.55 : 1 }}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={idx >= 0} onChange={() => toggle(v.id)} disabled={v.expired} inputProps={{ 'aria-label': format(t.videoShares.selectOne, { title: v.title }) }} />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {idx >= 0 && <Chip size="small" color="primary" label={idx + 1} sx={{ height: 18, fontSize: 11, minWidth: 22 }} />}
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                {v.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {v.filename}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {formatBytes(v.size_bytes)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(v.created_at)}</TableCell>
                        <TableCell>
                          <ExpiryChip expiresAt={v.expires_at} expired={v.expired} />
                        </TableCell>
                        <TableCell align="right">
                          <LinkActions url={v.share_url} onCopied={onCopied} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t.videoShares.deleteVideo} arrow>
                            <IconButton size="small" onClick={() => void removeVideo(v)} aria-label={format(t.videoShares.deleteOne, { title: v.title })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {videos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        {t.videoShares.empty}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Section title={t.videoShares.groupsTitle} description={t.videoShares.groupsDescription}>
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t.videoShares.groupTable.group}</TableCell>
                      <TableCell>{t.videoShares.groupTable.videos}</TableCell>
                      <TableCell>{t.videoShares.groupTable.layout}</TableCell>
                      <TableCell>{t.videoShares.groupTable.created}</TableCell>
                      <TableCell>{t.videoShares.groupTable.expires}</TableCell>
                      <TableCell align="right">{t.videoShares.groupTable.link}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((g) => {
                      const members = g.video_ids.map((id) => videosById.get(id)).filter((v): v is SharedVideo => !!v);
                      return (
                        <TableRow key={g.id} hover sx={{ opacity: g.expired ? 0.55 : 1 }}>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {g.title}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 380 }}>
                            <Tooltip title={members.map((m) => m.title).join(' · ')} arrow>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {format(members.length === 1 ? t.videoShares.videoCount : t.videoShares.videosCount, { count: members.length })}
                                {members.length > 0 && ` — ${members.map((m) => m.title).join(', ')}`}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                            {g.columns == null ? t.videoShares.layoutAutoShort : format(t.videoShares.perRow, { n: g.columns })}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(g.created_at)}</TableCell>
                          <TableCell>
                            <ExpiryChip expiresAt={g.expires_at} expired={g.expired} />
                          </TableCell>
                          <TableCell align="right">
                            <LinkActions url={g.share_url} onCopied={onCopied} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={t.videoShares.deleteGroup} arrow>
                              <IconButton size="small" onClick={() => void removeGroup(g)} aria-label={format(t.videoShares.deleteGroupOne, { title: g.title })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {groups.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          {t.videoShares.groupsEmpty}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Section>
        </>
      )}

      {uploading && data && (
        <UploadDialog maxBytes={data.max_video_bytes} onClose={() => setUploading(false)} onUploaded={() => void load()} />
      )}
      {grouping && (
        <GroupDialog
          videos={selectedVideos}
          onClose={() => setGrouping(false)}
          onCreated={() => {
            setSelected([]);
            void load();
          }}
        />
      )}
      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}
